import { Injectable, inject } from '@angular/core';
import { BehaviorSubject, combineLatest, filter, finalize, map, Observable, of, shareReplay, switchMap, switchMapTo, take, tap } from 'rxjs';
import { ApiServiceBase } from './ApiServiceBase';
import { AuthorizeService } from '../auth/authorize.service';
import { ProfileCreateModel } from '../common/ApplicationUserProfileForCreation';
import { ExtendedProfile } from '../common/ExtendedProfile';
import { IUser } from '../common/IUser.interface';
import { ProfileRole, ProfileRoleConstants } from '../common/ProfileRole';
import { ProfileStatus, ProfileStatusConstants } from '../common/ProfileStatus';
import { NonProfileUser } from '../common/NonProfileUser';
import { TfaSetupDto } from '../common/TfaSetupDto';
import { UserNameDto, UserNameUpdateRequest } from '../common/UserNameDto';
import { PagedResult, PagingParams, pagingToHttpParams } from '../common/PagedResult';

@Injectable({
  providedIn: 'root',
})
export class AppProfileService extends ApiServiceBase {
  private authService = inject(AuthorizeService);

  protected readonly path = 'profile';

  private _refreshProfile$ = new BehaviorSubject<void>(undefined);

  private _profileLoading$ = new BehaviorSubject<boolean>(false);

  // Emits `true` while the current profile is being fetched and `false` once it is settled.
  // Consumers (e.g. the app shell) use this to show a loading spinner during the fetch.
  public profileLoading$: Observable<boolean> = this._profileLoading$.asObservable();

  public currentProfile$: Observable<ExtendedProfile> = this._refreshProfile$.pipe(
    tap(() => this._profileLoading$.next(true)),
    switchMapTo(
      this.getCurrent().pipe(finalize(() => this._profileLoading$.next(false))),
    ),
    shareReplay(1),
  );

  public refreshCurrentProfile() {
    this._refreshProfile$.next();
  }

  /**
   * Refreshes and emits the profile that refresh produced, once (#199).
   *
   * Subscribing to `currentProfile$` right after `refreshCurrentProfile()` yields the **cached
   * previous value first** — `shareReplay` replays it before the new request has answered — so
   * a `take(1)` there decides on stale data (after sign-in, on the anonymous `null`: straight
   * to the welcome page). The login used to compensate by never unsubscribing and navigating
   * again on the second emission. This waits for `profileLoading$` to settle instead.
   */
  public reloadCurrentProfile(): Observable<ExtendedProfile> {
    const settled$ = combineLatest([this.profileLoading$, this.currentProfile$]).pipe(
      filter(([loading]) => !loading),
      take(1),
      map(([, profile]) => profile),
    );
    this.refreshCurrentProfile();
    return settled$;
  }

  public getUser(): Observable<IUser> {
    return this.authService.getUser().pipe(take(1));
  }

  public getAllOfCustomer(paging?: PagingParams): Observable<PagedResult<ExtendedProfile>> {
    return this.http
      .get<PagedResult<ExtendedProfile>>(this.getUrl(), {
        params: pagingToHttpParams(paging),
      })
      .pipe(
        // #648: the envelope arrives first — initialize the items, never the envelope.
        map((result) => ({
          totalCount: result.totalCount,
          items: result.items?.map((profile) => this.initializeProfile(profile)) ?? [],
        })),
      );
  }

  // #648: for callers needing the whole (small) profile set of the customer as
  // a plain array (client-side aggregation) — customers have at most a handful
  // of users, so one max-size page is exhaustive.
  public getAllOfCustomerList(): Observable<ExtendedProfile[]> {
    return this.getAllOfCustomer({ take: 500 }).pipe(map((result) => result.items));
  }

  public getById(id: string): Observable<ExtendedProfile> {
    return this.http
      .get<ExtendedProfile>(this.getUrl() + '/' + id)
      .pipe(
        map((profile) => this.initializeProfile(profile)),
      );
  }

  public getCurrent(): Observable<ExtendedProfile> {
    let url = this.getUrl() + '/current';

    // getUser() never completes and re-emits on every auth change, including transient `null`s
    // (see AuthorizeService.getUser). Take only its first, definitive emission: for a logged-in
    // user that is the user (→ fetch the profile), for a logged-out user it is `null` (→ no
    // profile). This avoids the transient `null` that made the profile guard flash the welcome
    // screen, while still settling — and completing — when nobody is signed in, so the loading
    // state clears after logout instead of hanging.
    return this.authService.getUser().pipe(
      take(1),
      switchMap((user) => {
        if (user) {
          return this.http
            .get<ExtendedProfile>(url)
            .pipe(map((profile) => this.initializeProfile(profile)));
        }
        return of(null);
      }),
    );
  }

  public getForCarrier(paging?: PagingParams): Observable<PagedResult<ExtendedProfile>> {
    return this.http
      .get<PagedResult<ExtendedProfile>>(this.getUrl() + '/forcarrier', {
        params: pagingToHttpParams(paging),
      })
      .pipe(
        map((result) => ({
          totalCount: result.totalCount,
          items: result.items?.map((profile) => this.initializeProfile(profile)) ?? [],
        })),
      );
  }

  public getDefaultTaC(): Observable<string> {
    return this.http
      .get(this.getUrl() + '/defaulttac', { responseType: 'text' });
  }

  public getNonConfirmedPrimaryUsers(): Observable<NonProfileUser[]> {
    return this.http
      .get<NonProfileUser[]>(this.getUrl() + '/nonconfirmedprimaryusers');
  }

  public getNonConfirmedSecondaryUsers(): Observable<NonProfileUser[]> {
    return this.http
      .get<NonProfileUser[]>(this.getUrl() + '/nonconfirmedsecondaryusers');
  }

  public create(appProfile: ProfileCreateModel): Observable<ProfileCreateModel> {
    return this.http
      .post<ProfileCreateModel>(
        this.getUrl(),
        appProfile
      );
  }

  public resendPrimaryUserEmailConfirmation(subject: string): Observable<any> {
    return this.http
      .post<void>(
        this.getUrl() + '/resendprimaryuseremailconf/' + encodeURIComponent(subject),
        null,
      );
  }

  public resendSecondaryUserEmailConfirmation(subject: string): Observable<any> {
    return this.http
      .post<void>(
        this.getUrl() + '/resendsecondaryuseremailconf/' + encodeURIComponent(subject),
        null,
      );
  }

  /**
   * Changes a profile's status and role; the rest of the model travels along and is ignored.
   *
   * Refused as data since backend #704 (PR #727): 400 `profileStatusInvalid` / `profileRoleInvalid`
   * at `Status` / `Role`, and 409 `profileLastAdmin` when the profile is its customer's last
   * approved administrator — for every caller, the Carrier included. Both carry the error
   * contract (#682 decision 2), so `CallApiService` words them; nothing here needs to.
   */
  public update(appProfile: ExtendedProfile): Observable<void> {
    return this.http.put<void>(this.getUrl(), appProfile);
  }

  public getProfileStates(): ProfileStatus[] {
    return [
      {
        Id: ProfileStatusConstants.StatusIdNameNew,
        DisplayName: ProfileStatusConstants.StatusDisplayNameNew,
      },
      {
        Id: ProfileStatusConstants.StatusIdNameApproved,
        DisplayName: ProfileStatusConstants.StatusDisplayNameApproved,
      },
      {
        Id: ProfileStatusConstants.StatusIdNameLocked,
        DisplayName: ProfileStatusConstants.StatusDisplayNameLocked,
      },
    ];
  }

  public getProfileRoles(): ProfileRole[] {
    return [
      {
        Id: ProfileRoleConstants.RoleIdDefault,
        DisplayName: ProfileRoleConstants.RoleDefaultDisplayName,
      },
      {
        Id: ProfileRoleConstants.RoleIdAdmin,
        DisplayName: ProfileRoleConstants.RoleAdminDisplayName,
      },
      {
        Id: ProfileRoleConstants.RoleIdNone,
        DisplayName: ProfileRoleConstants.RoleNoneDisplayName,
      },
    ];
  }

  public mapProfileDisplayNames(profiles: ExtendedProfile[]): ExtendedProfile[] {
    const states = this.getProfileStates();
    const roles = this.getProfileRoles();
    for (const profile of profiles) {
      profile.status = states.find((s) => s.Id == profile.status)?.DisplayName ?? profile.status;
      profile.role = roles.find((r) => r.Id == profile.role)?.DisplayName ?? profile.role;
    }
    return profiles;
  }

  // Self-service edit of the caller's own name claims (backend #571). Consumers refresh
  // the current profile afterwards — the avatar and profile page read the BFF profile.
  public updateUserName = (request: UserNameUpdateRequest) => {
    return this.http.put<UserNameDto>(`${this.getUrl()}/user-name`, request);
  };

  public getTfaSetup = () => {
    return this.http.get<TfaSetupDto>(`${this.getUrl()}/tfa-setup`);
  };

  public postTfaSetup = (body: TfaSetupDto) => {
    return this.http.post<TfaSetupDto>(`${this.getUrl()}/tfa-setup`, body);
  };

  public deleteProfile = () => {
    return this.http.delete<any>(`${this.getUrl()}`);
  };

  public disableTfa = () => {
    return this.http.delete<TfaSetupDto>(`${this.getUrl()}/tfa-setup`);
  };

  public deleteNonConfirmedPrimaryUser = (subject: string) => {
    return this.http.delete(`${this.getUrl()}/nonconfirmedprimaryusers`, {
      params: { subject },
    });
  };

  public deleteNonConfirmedSecondaryUser = (subject: string) => {
    return this.http.delete(`${this.getUrl()}/nonconfirmedsecondaryusers`, {
      params: { subject },
    });
  };

  public verifyTfa(code: string): Observable<any> {
    // The one body that stays hand-serialised: the backend takes `[FromBody] string`, i.e. a JSON
    // string literal. HttpClient would send a bare string unquoted as text/plain (415).
    return this.http.post<string>(`${this.getUrl()}/verifytfa`, JSON.stringify(code), {
      headers: { 'Content-Type': 'application/json' },
    });
  }

  // Add this helper method to initialize properties
  private initializeProfile(profile: ExtendedProfile): ExtendedProfile {
    if (!profile) return profile;
    // Example: Ensure a property is always an array
    profile.isActive = profile.customer.isActive;
    // Example: Set a default value
    profile.hasShopAccess =
      profile.status === ProfileStatusConstants.StatusIdNameApproved &&
      (profile.role === ProfileRoleConstants.RoleIdDefault ||
        profile.role === ProfileRoleConstants.RoleIdAdmin) &&
      profile.customer.isActive;
    // Add your custom initialization logic here
    return profile;
  }
}
